import React, { useEffect, useState } from "react";
import { LoadingActionButton, Toast } from "src/components";
import {
    ColorSwatch,
    SmallText,
    ColorRow,
    ColorButton,
    ModalCard,
    ModalHeaderBlock,
    ModalScroll,
    ModalFooter,
    ModalHeaderTop,
    ModalTitleBlock,
    ModalTitle,
    ModalSection,
    ModalSectionHeader,
    ModalSectionTitle,
    ModalSectionNote,
    FormGrid,
    VisibilityTargetCard,
    VisibilityTargetHeader,
    VisibilityTargetTitle,
    VisibilityTargetActions,
    Label,
    ModalCancelButton,
    TaskFieldInput,
    TaskFieldSelect,
    CalendarSelectWrap,
    CalendarSelectChevron,
} from "..";
import { SHARED_AUDIENCE } from "../../calendarData";
import { ITEM_TYPE_COLOR_OPTIONS } from "../../itemTypes";
import type {
    CalendarItem,
    CustomItemType,
    CustomItemTypeScope,
    ManagedItemType,
    TaskType,
    ThemeMode,
} from "../../types";
import { getCustomGroupNameLower } from "../../utils/calendarHelpers";

type ItemTypeManagerModalProps = {
    themeMode: ThemeMode;
    itemTypes: ManagedItemType[];
    customItemTypes: CustomItemType[];
    tasks: CalendarItem[];
    canManageCompanyItemTypes: boolean;
    onCreate: (itemType: CustomItemType) => boolean | Promise<boolean>;
    onUpdate: (itemType: CustomItemType) => boolean | Promise<boolean>;
    onDelete: (itemType: CustomItemType) => boolean | Promise<boolean>;
    onClose: () => void;
};

export function ItemTypeManagerModal({ themeMode, itemTypes, customItemTypes, tasks, canManageCompanyItemTypes, onCreate, onUpdate, onDelete, onClose }: ItemTypeManagerModalProps) {
    const [newItemType, setNewItemType] = useState<{ label: string; scope: CustomItemTypeScope; color: string }>({
        label: "",
        scope: canManageCompanyItemTypes ? "company" : "personal",
        color: ITEM_TYPE_COLOR_OPTIONS[0],
    });
    const [creatingItemType, setCreatingItemType] = useState(false);
    const [savingItemTypeId, setSavingItemTypeId] = useState<TaskType | null>(null);
    const [deletingItemTypeId, setDeletingItemTypeId] = useState<TaskType | null>(null);
    const [drafts, setDrafts] = useState<Record<string, { label: string; scope: CustomItemTypeScope; color: string }>>(() => customItemTypes.reduce((acc, itemType) => {
        acc[itemType.id] = {
            label: itemType.label,
            scope: itemType.scope,
            color: itemType.color,
        };
        return acc;
    }, {} as Record<string, { label: string; scope: CustomItemTypeScope; color: string }>));

    useEffect(() => {
        if (!canManageCompanyItemTypes && newItemType.scope === "company") {
            setNewItemType(prev => ({ ...prev, scope: "personal" }));
        }
    }, [canManageCompanyItemTypes, newItemType.scope]);

    useEffect(() => {
        setDrafts(customItemTypes.reduce((acc, itemType) => {
            acc[itemType.id] = {
                label: itemType.label,
                scope: itemType.scope,
                color: itemType.color,
            };
            return acc;
        }, {} as Record<string, { label: string; scope: CustomItemTypeScope; color: string }>));
    }, [customItemTypes]);

    const getTasksUsingType = (id: TaskType) => tasks.filter(task => task.taskType === id);
    const validateItemTypeScope = (id: TaskType, scope: CustomItemTypeScope): boolean => {
        const tasksUsingType = getTasksUsingType(id);
        const invalidUse = tasksUsingType.find(task => scope === "personal" ? task.audience !== SHARED_AUDIENCE.PERSONAL : task.audience === SHARED_AUDIENCE.PERSONAL);
        if (invalidUse) {
            Toast.error(scope === "personal"
                ? "Personal item types can only be used by personal items."
                : `Company item types can only be used by items visible to ${getCustomGroupNameLower("employees")} or ${getCustomGroupNameLower("clients")}.`);
            return false;
        }
        return true;
    };

    const itemTypeNameExists = (label: string, ignoreId?: TaskType) => itemTypes.some(itemType => itemType.id !== ignoreId && itemType.label.trim().toLowerCase() === label.trim().toLowerCase());

    const addItemType = async () => {
        const label = newItemType.label.trim();
        if (!label) {
            Toast.error("Item type name is required.");
            return;
        }
        if (itemTypeNameExists(label)) {
            Toast.error("An item type with this name already exists.");
            return;
        }
        if (newItemType.scope === "company" && !canManageCompanyItemTypes) {
            Toast.error("Only users with Calendar permission can create company-wide item types.");
            return;
        }
        setCreatingItemType(true);
        try {
            if (await onCreate({ id: "", itemTypeUuid: "", label, scope: newItemType.scope, color: newItemType.color })) {
                setNewItemType({ label: "", scope: canManageCompanyItemTypes ? "company" : "personal", color: ITEM_TYPE_COLOR_OPTIONS[0] });
            }
        } finally {
            setCreatingItemType(false);
        }
    };

    const updateItemType = async (id: TaskType) => {
        const draft = drafts[id];
        const itemType = customItemTypes.find(customItemType => customItemType.id === id);
        if (!draft) return;
        if (!itemType) return;
        if ((itemType.scope === "company" || draft.scope === "company") && !canManageCompanyItemTypes) {
            Toast.error("Only users with Calendar permission can manage company-wide item types.");
            return;
        }
        const label = draft.label.trim();
        if (!label) {
            Toast.error("Item type name is required.");
            return;
        }
        if (itemTypeNameExists(label, id)) {
            Toast.error("An item type with this name already exists.");
            return;
        }
        if (!validateItemTypeScope(id, draft.scope)) return;
        setSavingItemTypeId(id);
        try {
            await onUpdate({ ...itemType, ...draft, label });
        } finally {
            setSavingItemTypeId(null);
        }
    };

    const deleteItemType = async (id: TaskType) => {
        const itemType = customItemTypes.find(customItemType => customItemType.id === id);
        if (!itemType) return;
        if (itemType.scope === "company" && !canManageCompanyItemTypes) {
            Toast.error("Only users with Calendar permission can manage company-wide item types.");
            return;
        }
        if (getTasksUsingType(id).length > 0) {
            Toast.error("This item type is being used by existing calendar items.");
            return;
        }
        setDeletingItemTypeId(id);
        try {
            await onDelete(itemType);
        } finally {
            setDeletingItemTypeId(null);
        }
    };

    return (
        <ModalCard themeMode={themeMode}>
            <ModalHeaderBlock>
                <ModalHeaderTop>
                    <ModalTitleBlock>
                        <ModalTitle>Manage Item Types</ModalTitle>
                    </ModalTitleBlock>
                </ModalHeaderTop>
            </ModalHeaderBlock>
            <ModalScroll>
                <ModalSection themeMode={themeMode}>
                    <ModalSectionHeader>
                        <ModalSectionTitle>Add Item Type</ModalSectionTitle>
                        <ModalSectionNote>
                            Company types are available for items visible to {getCustomGroupNameLower("employees")} or {getCustomGroupNameLower("clients")}. Personal types are only available for personal items.
                        </ModalSectionNote>
                    </ModalSectionHeader>
                    <FormGrid>
                        <div>
                            <Label>Name</Label>
                            <TaskFieldInput themeMode={themeMode} value={newItemType.label} onChange={({ target: { value } }) => setNewItemType(prev => ({ ...prev, label: value }))} placeholder="Item type name" />
                        </div>
                        <div>
                            <Label>Availability</Label>
                            <CalendarSelectWrap $fullWidth>
                                <TaskFieldSelect themeMode={themeMode} value={newItemType.scope} onChange={({ target: { value } }) => setNewItemType(prev => ({ ...prev, scope: value as CustomItemTypeScope }))}>
                                    <option value="company" disabled={!canManageCompanyItemTypes}>Company-wide</option>
                                    <option value="personal">Personal only</option>
                                </TaskFieldSelect>
                                <CalendarSelectChevron aria-hidden="true">›</CalendarSelectChevron>
                            </CalendarSelectWrap>
                        </div>
                    </FormGrid>
                    <div>
                        <Label>Colour</Label>
                        <ColorRow>
                            {ITEM_TYPE_COLOR_OPTIONS.map(color => (
                                <ColorButton key={color} type="button" color={color} active={newItemType.color === color} onClick={() => setNewItemType(prev => ({ ...prev, color }))} />
                            ))}
                        </ColorRow>
                    </div>
                    <div>
                        <LoadingActionButton
                            type="button"
                            isLoading={creatingItemType}
                            disabled={creatingItemType}
                            minWidth="120px"
                            height="42px"
                            borderRadius="var(--calendar-radius-control)"
                            backgroundColor="var(--calendar-accent)"
                            color="#ffffff"
                            font="normal normal 800 12px/16px Inter, system-ui, sans-serif"
                            onClick={addItemType}
                        >
                            Add Item Type
                        </LoadingActionButton>
                    </div>
                </ModalSection>
                <ModalSection themeMode={themeMode}>
                    <ModalSectionHeader>
                        <ModalSectionTitle>Available Item Types</ModalSectionTitle>
                        <ModalSectionNote>Built-in item types are available to everyone and cannot be changed.</ModalSectionNote>
                    </ModalSectionHeader>
                    <div style={{ display: "grid", gap: 10 }}>
                        {itemTypes.map((itemType) => {
                            const draft = drafts[itemType.id];
                            const isBuiltIn = itemType.scope === "builtIn";
                            const canModifyItemType = isBuiltIn || itemType.scope !== "company" || canManageCompanyItemTypes;
                            return (
                                <VisibilityTargetCard key={itemType.id}>
                                    <VisibilityTargetHeader style={{ alignItems: isBuiltIn ? "center" : "start" }}>
                                        <div style={{ minWidth: 0 }}>
                                            <VisibilityTargetTitle>
                                                <ColorSwatch color={isBuiltIn ? itemType.color : draft?.color || itemType.color} />
                                                {isBuiltIn ? itemType.label : (
                                                    <TaskFieldInput
                                                        themeMode={themeMode}
                                                        value={draft?.label || ""}
                                                        disabled={!canModifyItemType}
                                                        onChange={({ target: { value } }) => setDrafts(prev => ({ ...prev, [itemType.id]: { ...prev[itemType.id], label: value } }))}
                                                    />
                                                )}
                                            </VisibilityTargetTitle>
                                            <SmallText style={!isBuiltIn ? { marginTop: "0.5rem", marginLeft: "calc(14px + 0.55rem)" } : undefined}>
                                                {isBuiltIn ? "Built-in type" : `${getTasksUsingType(itemType.id).length} item(s) using this type`}
                                            </SmallText>
                                            {!isBuiltIn && draft && (
                                                <ColorRow style={{ marginTop: "0.5rem", marginLeft: "calc(14px + 0.55rem)", maxWidth: 270 }}>
                                                    {ITEM_TYPE_COLOR_OPTIONS.map(color => (
                                                        <ColorButton key={color} type="button" color={color} active={draft.color === color} disabled={!canModifyItemType} onClick={() => setDrafts(prev => ({ ...prev, [itemType.id]: { ...prev[itemType.id], color } }))} />
                                                    ))}
                                                </ColorRow>
                                            )}
                                        </div>
                                        {!isBuiltIn && draft && (
                                            <VisibilityTargetActions style={{ alignItems: "unset", alignSelf: "stretch", display: "grid", justifyItems: "end", minWidth: 280, WebkitAlignItems: "unset" }}>
                                                <CalendarSelectWrap $fullWidth>
                                                    <TaskFieldSelect themeMode={themeMode} value={draft.scope} disabled={!canModifyItemType} onChange={({ target: { value } }) => setDrafts(prev => ({ ...prev, [itemType.id]: { ...prev[itemType.id], scope: value as CustomItemTypeScope } }))}>
                                                        <option value="company" disabled={!canManageCompanyItemTypes}>Company-wide</option>
                                                        <option value="personal">Personal only</option>
                                                    </TaskFieldSelect>
                                                    <CalendarSelectChevron aria-hidden="true" style={{ marginTop: "-6px" }}>›</CalendarSelectChevron>
                                                </CalendarSelectWrap>
                                                <div style={{ alignSelf: "end", display: "flex", gap: "0.55rem", justifyContent: "flex-end" }}>
                                                    <LoadingActionButton
                                                        type="button"
                                                        isLoading={savingItemTypeId === itemType.id}
                                                        disabled={!canModifyItemType || savingItemTypeId === itemType.id || deletingItemTypeId === itemType.id}
                                                        minWidth="76px"
                                                        height="38px"
                                                        borderRadius="var(--calendar-radius-control)"
                                                        border="1px solid var(--calendar-border-strong)"
                                                        background={themeMode === "dark" ? "rgba(19, 25, 39, 0.9)" : "rgba(255, 255, 255, 0.92)"}
                                                        color="var(--calendar-text)"
                                                        font="normal normal 700 12px/16px Inter, system-ui, sans-serif"
                                                        onClick={() => updateItemType(itemType.id)}
                                                    >
                                                        Save
                                                    </LoadingActionButton>
                                                    <LoadingActionButton
                                                        type="button"
                                                        isLoading={deletingItemTypeId === itemType.id}
                                                        disabled={!canModifyItemType || savingItemTypeId === itemType.id || deletingItemTypeId === itemType.id}
                                                        minWidth="76px"
                                                        height="38px"
                                                        borderRadius="10px"
                                                        border="1px solid #ff5353"
                                                        background="transparent"
                                                        color="#ff5353"
                                                        font="normal normal 700 0.875rem/16px Inter, system-ui, sans-serif"
                                                        onClick={() => deleteItemType(itemType.id)}
                                                    >
                                                        Delete
                                                    </LoadingActionButton>
                                                </div>
                                            </VisibilityTargetActions>
                                        )}
                                    </VisibilityTargetHeader>
                                </VisibilityTargetCard>
                            );
                        })}
                    </div>
                </ModalSection>
            </ModalScroll>
            <ModalFooter themeMode={themeMode} style={{ justifyContent: "flex-end" }}>
                <ModalCancelButton themeMode={themeMode} type="button" onClick={onClose}>Close</ModalCancelButton>
            </ModalFooter>
        </ModalCard>
    );
}
